<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'

const INDICATOR_TYPES = ['dot']
type IndicatorType = typeof INDICATOR_TYPES[number]

const INDICATOR_COLORS = ['white', 'gray', 'lightGray', 'orange']
type IndicatorColor = typeof INDICATOR_TYPES[number]

const INDICATOR_SIZES = ['small', 'normal', 'large']
type IndicatorSize = typeof INDICATOR_TYPES[number]

export default defineComponent({
  name: 'ProgressIndicator',

  props: {
    /**
     * ローディングインジケータの種類
     * @description 現状はドットインジケータしか用意してないが、パターン追加ごとにこちらに追記
     */
    type: {
      type: String as PropType<IndicatorType>,
      default: 'dot',
    },

    /**
     * インジケータの色
     */
    color: {
      type: String as PropType<IndicatorColor>,
      default: 'gray',
    },

    /**
     * インジケータのサイズ
     */
    size: {
      type: String as PropType<IndicatorSize>,
      default: 'normal',
    },
  },

  setup(props) {
    const indicatorStateClassName = computed<Array<string>>(() => {
      const type = INDICATOR_TYPES.includes(props.type)
        ? `is-${props.type}`
        : 'is-dot'

      const color = INDICATOR_COLORS.includes(props.color)
        ? `is-${props.color}`
        : 'is-gray'

      const size = INDICATOR_SIZES.includes(props.size)
        ? `is-${props.size}`
        : 'is-normal'

      return [type, color, size]
    })

    return {
      // computed
      indicatorStateClassName,
    }
  }
})
</script>

<template>
  <div
    :class="[
      'o-figure__loading',
      ...indicatorStateClassName,
    ]">
    <p class="o-figure__loading--item">
      <slot>
        しばらくお待ち下さい
      </slot>
    </p>
  <!-- /.o-figure__loading --></div>
</template>

<style lang="sass" scoped>
$small: 0.5em
$normal: 1em
$large: 1.5em
=loadingIndicatorAnimation($root, $size)
  #{$root}--item,
  #{$root}--item::before,
  #{$root}--item::after
    width: $size
    height: $size

  #{$root}--item
    top: -$size

  #{$root}--item::before
    left: -($size + 1)

  #{$root}--item::after
    left: ($size + 1)

.o-figure__loading
  $root: &

  &.is-dot
    width: 100%
    position: relative
    margin: 0px auto

    #{$root}--item,
    #{$root}--item::before,
    #{$root}--item::after
      border-radius: 50%
      -webkit-animation:
        duration: 1.8s
        iteration-count: infinite
        timing-function: ease-in-out
        fill-mode: both
      animation:
        duration: 1.8s
        iteration-count: infinite
        timing-function: ease-in-out
        fill-mode: both

    #{$root}--item
      margin: auto
      position: relative
      text-indent: -9999em
      -webkit-transform: translateZ(0)
      -ms-transform: translateZ(0)
      transform: translateZ(0)
      -webkit-animation-delay: -0.16s
      animation-delay: -0.16s

    #{$root}--item::before,
    #{$root}--item::after
      content: ""
      position: absolute
      top: 0

    #{$root}--item::before
      -webkit-animation-delay: -0.32s
      animation-delay: -0.32s

  &.is-normal
    +loadingIndicatorAnimation($root, $normal)

    #{$root}--item,
    #{$root}--item::before,
    #{$root}--item::after
      -webkit-animation-name: loadingIndicatorNormal
      animation-name: loadingIndicatorNormal

  &.is-small
    +loadingIndicatorAnimation($root, $small)

    #{$root}--item,
    #{$root}--item::before,
    #{$root}--item::after
      -webkit-animation-name: loadingIndicatorSmall
      animation-name: loadingIndicatorSmall

  &.is-large
    +loadingIndicatorAnimation($root, $large)

    #{$root}--item,
    #{$root}--item::before,
    #{$root}--item::after
      -webkit-animation-name: loadingIndicatorLarge
      animation-name: loadingIndicatorLarge

  &.is-white
    #{$root}--item
      color: $font-white

  &.is-gray
    #{$root}--item
      color: $font-x-dark-gray

  &.is-lightGray
    #{$root}--item
      color: $font-xx-light-gray

  &.is-orange
    #{$root}--item
      color: $font-orange

  @-webkit-keyframes loadingIndicatorNormal
    0%,
    80%,
    100%
      box-shadow: 0 $normal 0 (-(math.div($normal, 2)))

    40%
      box-shadow: 0 $normal 0 0

  @keyframes loadingIndicatorNormal
    0%,
    80%,
    100%
      box-shadow: 0 $normal 0 (-(math.div($normal, 2)))

    40%
      box-shadow: 0 $normal 0 0

  @-webkit-keyframes loadingIndicatorSmall
    0%,
    80%,
    100%
      box-shadow: 0 $small 0 (-(math.div($small, 2)))

    40%
      box-shadow: 0 $small 0 0

  @keyframes loadingIndicatorSmall
    0%,
    80%,
    100%
      box-shadow: 0 $small 0 (-(math.div($small, 2)))

    40%
      box-shadow: 0 $small 0 0

  @-webkit-keyframes loadingIndicatorLarge
    0%,
    80%,
    100%
      box-shadow: 0 $large 0 (-(math.div($large, 2)))

    40%
      box-shadow: 0 $large 0 0

  @keyframes loadingIndicatorLarge
    0%,
    80%,
    100%
      box-shadow: 0 $large 0 (-(math.div($large, 2)))

    40%
      box-shadow: 0 $large 0 0
</style>
